import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  initialTarificationState,
  tarificationReducer,
  TarificationState,
} from "store/reducers/tarification.reducer";
import {
  initialTenantContextState,
  tenantContextReducer,
} from "store/reducers/tenant-context.reducer";
import { TenantContext } from "store/types/tenant-context.types";
import thunk from "redux-thunk";
import {
  initialUserContextState,
  userContextReducer,
  UserContextState,
} from "store/reducers/user-context.reducer";
import {
  initialPatientContextState,
  patientReducer,
  PatientState,
} from "store/reducers/patient.reducer";
import {
  helenaReducer,
  HelenaState,
  initialHelenaState,
} from "store/reducers/helena.reducer";
import { createLogger } from "redux-logger";
import {
  initialNotificationContextState,
  notificationReducer,
  NotificationState,
} from "./reducers/notification.reducer";
import {
  initialJournalState,
  journalReducer,
  JournalState,
} from "./reducers/journal.reducer";
import {
  initialMedicationState,
  medicationReducer,
  MedicationState,
} from "./reducers/medication.reducer";
import {
  initialSessionState,
  sessionReducer,
  SessionState,
} from "store/reducers/session.reducer";
import createSagaMiddleware from "redux-saga";
import { promiseMiddleware } from "@adobe/redux-saga-promise";
import { rootSaga } from "store/root.saga";

export interface GlobalState {
  journal: JournalState;
  patientContext: PatientState;
  tarification: TarificationState;
  userContext: UserContextState;
  helena: HelenaState;
  tenantContext: TenantContext;
  notificationContext: NotificationState;
  medicationContext: MedicationState;
  sessionContext: SessionState;
}

export const initialState: GlobalState = {
  journal: initialJournalState,
  patientContext: initialPatientContextState,
  tarification: initialTarificationState,
  userContext: initialUserContextState,
  helena: initialHelenaState,
  tenantContext: initialTenantContextState,
  notificationContext: initialNotificationContextState,
  medicationContext: initialMedicationState,
  sessionContext: initialSessionState,
};

const rootReducer = combineReducers({
  journal: journalReducer,
  patientContext: patientReducer,
  tarification: tarificationReducer,
  userContext: userContextReducer,
  helena: helenaReducer,
  tenantContext: tenantContextReducer,
  notificationContext: notificationReducer,
  medicationContext: medicationReducer,
  sessionContext: sessionReducer,
});

const STATE_VERSION = 12;

const persistConfig = {
  key: "visitStore",
  version: STATE_VERSION,
  storage,
  blacklist: process.env.NODE_ENV === `development` ? [] : ["sessionContext"],
  migrate: (state: any) => {
    if (state && state["_persist"].version !== STATE_VERSION) {
      return Promise.resolve({ ...initialState });
    } else {
      return Promise.resolve(state);
    }
  },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const composeEnhancers =
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const middlewares = [];
middlewares.push(thunk);
middlewares.push(promiseMiddleware);
const sagaMiddleware = createSagaMiddleware();
middlewares.push(sagaMiddleware);

// WARNING [DSA]: redux logger should be last middleware
if (process.env.NODE_ENV === `development`) {
  const logger = createLogger({
    collapsed: true,
  });
  middlewares.push(logger);
}

export const store = createStore(
  persistedReducer,
  composeEnhancers(applyMiddleware(...middlewares))
);

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);
